// React & Vendor Libs
const { useState, useMemo, useEffect, useCallback } = wp.element;

// NekoUI
import { NekoButton, NekoBlock, NekoWrapper, NekoColumn, NekoTable, useNekoTasks, NekoPaging,
  NekoQuickLinks, NekoLink, NekoTheme, NekoCheckbox, NekoTypo, NekoIcon } from '@neko-ui';
import { formatBytes, useKeyboardEvent } from '@neko-ui';
import useDatabaseCleanerContext, { optionsColumns, optionsFilters as filters, hideUsedByWordPressLabel } from '../context';
import i18n from '../i18n';
import { SectionButtonStyle, limitSelectElement, SectionFooterStyle,
  FilterSearchField, UsedByField, BulkUsedByButton, NameField } from './common';
import { useShiftSelectable } from './hooks';

const usedByKey = 'option_usedby';
const SettingsOptionsTables = () => {

  const { busyAction, filteredOptions, busyCleanBulkTasks, busyOptions,
    optionsFilters, optionsTotal: total, optionsUsedBy, plugins } = useDatabaseCleanerContext();
  const { deleteOption, switchAutoloadedOption, resetBusyBulkTasks, updateOption, toggleModal, setLoadEntryItem,
    setBusyAction, setShouldFetchOptions, updateOptionsFilters, mutateOptions, setEntries, getOptionValue,
    pushBusyOptions, popBusyOptions, deleteOptions, setSelectedUsedBy } = useDatabaseCleanerContext();
  const bulkTasks = useNekoTasks();

  const [ selectedOptionNames, setSelectedOptionNames ] = useState([]);
  const allChecked = useMemo(() => selectedOptionNames.length === filteredOptions.length, [selectedOptionNames, filteredOptions]);

  const { pressShift } = useKeyboardEvent();
  const { onCheckboxChange } = useShiftSelectable({
    pressShift,
    list: filteredOptions,
    selectedList: selectedOptionNames,
    setSelectedList: setSelectedOptionNames,
    key: 'option_name'
  });

  useEffect(() => {
    setShouldFetchOptions();
  }, []);

  useEffect(() => {
    return () => {
      if (busyCleanBulkTasks) { 
        return;
      }
      bulkTasks.stop();
      resetBusyBulkTasks();
      setBusyAction([]);
    }
  }, [busyCleanBulkTasks]);

  const deleteBusyItems = useMemo(() => {
    const optionNames = filteredOptions.map(v => v.option_name);
    return busyAction.filter(busy => optionNames.includes(busy.item));
  }, [busyAction, filteredOptions]);

  const isBulkDeleteDisabled = useMemo(() =>
    selectedOptionNames.some(v => v.slug === 'wordpress'),
    [selectedOptionNames]
  );

  const autoloadOptionsData = useMemo(() => {
    return (
      filteredOptions.map(v => {
        const checked = selectedOptionNames.some(option => option.name === v.option_name);
        const onChange = (isChecked, id) => onCheckboxChange(isChecked, v)
        const isAutoloaded = v.autoload === 'yes';
        const onAutoloadedChange = (value) => {
          switchAutoloadedOption({ item: v.option_name, autoload: value ? 'yes' : 'no' });
        }
        const disabled = busyAction.some(busy => busy.item === v.option_name);
        const isLoadingOptionValue = busyAction.some(busy => busy.item === `loading_${v.option_name}`);

        const isUsed = v.info?.status === 'ok';
        //const isUsedByWP = v.info?.usedBy.toLowerCase() === 'wordpress';

        const onEyeButtonClicked = async () => {
          setEntries([]);
          await getOptionValue({ optionName: v.option_name });
          toggleModal({modal: 'optionValue', enable: true, title: `Option: ${v.option_name}`});
        }

        return {
          name: <NameField name={v.option_name} item={v} itemText={v.option_name}
            onChange={onChange} checked={checked} isBusy={checked && disabled} />,
          usedBy: <UsedByField item={v} itemName={v.option_name} itemsUsedBy={optionsUsedBy}
            optionName={usedByKey} plugins={plugins} setSelectedUsedBy={setSelectedUsedBy}
            toggleModal={toggleModal} updateOption={updateOption} onRefresh={() => { onRefresh(); }}
          />,
          size: <span style={{ display: 'inline-flex', justifyItem: 'center', alignItems: 'center' }}>
            <NekoTypo>{formatBytes(v.option_value_length)}</NekoTypo>
            <NekoIcon onClick={onEyeButtonClicked} icon="search" color="#007cba"
              style={{ width: 20, marginLeft: 5, cursor: 'pointer' }} isBusy={isLoadingOptionValue} />
          </span>,
          autoloaded: <div style={{ display: 'flex', justifyContent: 'right' }}>
            <NekoCheckbox name={`${v.option_name}-autoloaded`} checked={isAutoloaded} onChange={onAutoloadedChange} />
          </div>,
          actions: <NekoButton className={isUsed ? 'primary' : 'danger'} 
            onClick={() => deleteOption({ item: v.option_name })} icon="close" busyIconSize="100%"
            style={{ minWidth: 40, width: 40 }} disabled={disabled || isUsed} isBusy={disabled}>
          </NekoButton>
        }
      })
    );
  }, [filteredOptions, busyAction, selectedOptionNames, optionsUsedBy, onCheckboxChange]);

  const onUpdateLimit = useCallback(async(value, name) => {
    updateOption({ param: { name, value }});
  }, [updateOption]);

  const onRefresh = useCallback(async (clearSelectedItems = false) => {
    pushBusyOptions();
    await mutateOptions();
    if (clearSelectedItems) {
      setSelectedOptionNames([]);
    }
    popBusyOptions();
  }, [mutateOptions]);

  const onBulkDeleteClick = useCallback(() => {
    toggleModal({modal: 'deleteAll', enable: true, onNext: async () => {
      const newSelectedOptionNames = await deleteOptions({ items: selectedOptionNames.map(v => v.name) });
      if (newSelectedOptionNames.length) {
        setSelectedOptionNames(newSelectedOptionNames);
        return;
      }
      setSelectedOptionNames([]);
    }});
  }, [selectedOptionNames]);

  const columns = useMemo(() => {
    const i = optionsColumns.findIndex(v => v.accessor === 'name');
    const j = optionsColumns.findIndex(v => v.accessor === 'actions');
    const onChange = (checked) => {
      setSelectedOptionNames(checked ? filteredOptions.map(v => {
        return { name: v.option_name, info: v.info };
      }) : [])
    }
    optionsColumns[i].title = <>
      <NekoCheckbox name="all-options" checked={allChecked} onChange={onChange} />
      Name
    </>;
    optionsColumns[j].title = '';

    if (!selectedOptionNames.length)
      return optionsColumns;

    const actionsTitle = <>
      <NekoButton icon="close" title={i18n.INFO_BULK_DELETE_ITEMS} style={{ minWidth: 40, width: 40 }}
        className={!isBulkDeleteDisabled ? 'danger' : 'primary'} busyIconSize="100%"
        onClick={onBulkDeleteClick} isBusy={deleteBusyItems.length > 1}
        disabled={deleteBusyItems.length||isBulkDeleteDisabled} />
    </>;
    optionsColumns[j].title = actionsTitle;
    return optionsColumns;
  }, [optionsColumns, allChecked, filteredOptions, selectedOptionNames, deleteBusyItems, isBulkDeleteDisabled, onBulkDeleteClick]);

  const limitSelect = useMemo(() => {
    return limitSelectElement("options_limit", optionsFilters.limit, (value, name) => {
      setSelectedOptionNames([]);
      updateOptionsFilters({ limit: value, page: 1 });
      onUpdateLimit(value, name);
    });
  }, [optionsFilters.limit, updateOptionsFilters, onUpdateLimit]);

  const paging = useMemo(() => {
    const { page, limit, filterBy, hideUsedByWordPress } = optionsFilters;
    const pagingTotal = total[filterBy][hideUsedByWordPress];
    return <NekoPaging currentPage={page} limit={limit} total={pagingTotal} onClick={(page) => { setSelectedOptionNames([]); updateOptionsFilters({ page }); }} />
  }, [optionsFilters.page, optionsFilters.limit, total, updateOptionsFilters]);

  const currentSort = useMemo(() => {
    return { by: optionsFilters.sort, accessor: optionsFilters.accessor };
  }, [optionsFilters.accessor, optionsFilters.sort]);

  useEffect(() => {
    setSelectedOptionNames([]);
  }, [currentSort]);

  return (
    <NekoWrapper>

      <NekoColumn minimal>
        <NekoBlock busy={busyOptions} title="Options" className="primary" action={
          <BulkUsedByButton itemsUsedBy={optionsUsedBy} optionName='option_usedby' selectedItems={selectedOptionNames} plugins={plugins}
            setSelectedUsedBy={setSelectedUsedBy} toggleModal={toggleModal} updateOption={updateOption} onRefresh={() => { onRefresh(true); }} />
        }>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <NekoQuickLinks value={optionsFilters.filterBy} 
              onChange={(value) => { setSelectedOptionNames([]); updateOptionsFilters({ filterBy: value, page: 1 }); }}>
              {filters.map(v => 
                <NekoLink title={v.title} value={v.value} count={total[v.value][optionsFilters.hideUsedByWordPress]}
                  variant={v.filterValue === 'warn' && total[v.value][optionsFilters.hideUsedByWordPress] > 0 ? 'danger' : 'primary'} />)
              }
            </NekoQuickLinks>
            <div style={{ display: 'flex' }}>
              <NekoButton icon="replay" color={NekoTheme.white} style={{...SectionButtonStyle, color: NekoTheme.blue}} onClick={onRefresh} />
              {paging}
            </div>
          </div>
          <NekoTable variant="raw" alternateRowColor data={autoloadOptionsData} columns={columns} selectedItems={[]} sort={currentSort} sortIconColor={NekoTheme.blue}
            onSortChange={(accessor, sort) => updateOptionsFilters({ accessor, sort })} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            {limitSelect}
            {paging}
          </div>
          <div style={SectionFooterStyle}>
            <FilterSearchField name='options_search' value={optionsFilters.search}
              onChange={(value) => updateOptionsFilters({ search: value, page: 1 })}
              onReset={() => updateOptionsFilters({ search: "" })}
            />
            <div style={{ flex: 'auto' }} />
            <NekoCheckbox name="options_hide_wordpress" label={hideUsedByWordPressLabel}
              checked={optionsFilters.hideUsedByWordPress}
              onChange={(value) => updateOptionsFilters({ hideUsedByWordPress: value, page: 1 })}
            />
          </div>
        </NekoBlock>
      </NekoColumn>

    </NekoWrapper>
  );
};

export default SettingsOptionsTables;